/**
 * Einstellungen, auf dem Gerät gespeichert.
 *
 * Wie der Lernstand liegt das im localStorage und geht nicht ins Netz. Gespeichert wird
 * nur, was der Nutzer selbst gewählt hat; fehlt etwas oder ist es beschädigt, gilt der
 * Vorgabewert, denn eine kaputte Einstellung darf die App nicht anhalten.
 */
import type { Lang } from './i18n'
import { LANGS } from './i18n'

export type ViewMode = 'night' | 'day' | 'red'
export type IalaRegion = 'A' | 'B'
export type Units = 'nm' | 'km'

/**
 * Welche Ordnung der Katalog zeigt: die KVR auf See oder die BinSchStrO auf den deutschen
 * Binnenwasserstraßen. Die beiden widersprechen sich in der Sache, deshalb sind sie
 * getrennt und nicht gemischt.
 */
export type Bereich = 'see' | 'binnen'

export interface Settings {
  lang: Lang
  region: IalaRegion
  units: Units
  mode: ViewMode
  bereich: Bereich
  /**
   * Mondphase, 0 ist Neumond und 1 ist Vollmond. Sie entscheidet, wie viel man nachts
   * vom Rumpf sieht: bei Neumond bleiben nur die Lichter übrig, und das ist die Lage,
   * für die diese App übt.
   */
  mondphase: number
  /**
   * Hat der Nutzer beim ersten Aufruf gesagt, was er können will? Solange nicht, zeigt
   * die App nur diese eine Frage. Ohne sie weiß sie nicht, welchen Bestand sie üben soll,
   * und der Nutzer weiß nicht, wofür die App gut ist.
   */
  eingerichtet: boolean
  /** Kauf freigeschaltet. Ohne Kaufschranke im Build ist der Wert bedeutungslos. */
  unlocked: boolean
}

const KEY = 'colregs3d.settings.v1'

/**
 * Vorgabe: Sprache aus dem Browser, sonst Englisch. Der Zielmarkt ist weltweit, Deutsch
 * ist der Sonderfall, nicht die Norm.
 */
export function defaultSettings(language = globalThis.navigator?.language ?? 'en'): Settings {
  const lang = LANGS.find((l) => language.toLowerCase().startsWith(l)) ?? 'en'
  return { lang, region: 'A', units: 'nm', mode: 'night', bereich: 'see', mondphase: 1, eingerichtet: false, unlocked: false }
}

export function parseSettings(raw: unknown, fallback = defaultSettings()): Settings {
  if (typeof raw !== 'object' || raw === null) return fallback
  const value = raw as Partial<Settings>
  return {
    lang: LANGS.includes(value.lang as Lang) ? (value.lang as Lang) : fallback.lang,
    region: value.region === 'B' ? 'B' : value.region === 'A' ? 'A' : fallback.region,
    units: value.units === 'km' ? 'km' : value.units === 'nm' ? 'nm' : fallback.units,
    mode:
      value.mode === 'day' || value.mode === 'red' || value.mode === 'night'
        ? value.mode
        : fallback.mode,
    bereich: value.bereich === 'binnen' ? 'binnen' : value.bereich === 'see' ? 'see' : fallback.bereich,
    mondphase:
      typeof value.mondphase === 'number' && value.mondphase >= 0 && value.mondphase <= 1
        ? value.mondphase
        : fallback.mondphase,
    eingerichtet: value.eingerichtet === true,
    unlocked: value.unlocked === true,
  }
}

export function loadSettings(): Settings {
  try {
    const raw = globalThis.localStorage?.getItem(KEY)
    return raw ? parseSettings(JSON.parse(raw)) : defaultSettings()
  } catch {
    return defaultSettings()
  }
}

export function saveSettings(settings: Settings): void {
  try {
    globalThis.localStorage?.setItem(KEY, JSON.stringify(settings))
  } catch {
    // Privater Modus oder volle Ablage: die App läuft weiter, nur ohne Gedächtnis.
  }
}

/**
 * Die fünf Stufen der Mondphase, von Neumond bis Vollmond.
 *
 * Fünf Stufen statt eines stufenlosen Reglers: die Mondphase ist eine Lage, die man
 * erkennt, kein Wert, den man einstellt. Sie steht hier und nicht in einer Ansicht, weil
 * zwei Stellen sie anbieten: die Szene, wo sie die Schwierigkeit der Übung ist, und die
 * Einstellungen, wo sie zu den übrigen Voreinstellungen gehört.
 */
export const MONDPHASEN: readonly (readonly [number, string])[] = [
  [0, 'mondphase.neu'],
  [0.25, 'mondphase.sichel'],
  [0.5, 'mondphase.halb'],
  [0.75, 'mondphase.dreiviertel'],
  [1, 'mondphase.voll'],
] as const
